import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FocusEvent } from 'react';
import { Link } from 'react-router-dom';

import { cx, focusRingClass, interactiveClass } from '@/components/ui/shared';
import type { ConsoleRouteLink } from '@/navigation/consoleNavigation';
import type { ConsoleRouteId } from '@/navigation/consoleRoutePaths';

interface ConsoleNavigationMenuProps {
  label: string;
  routes: readonly ConsoleRouteLink[];
  activeRouteId: ConsoleRouteId | null;
  onNavigate?: () => void;
}

/**
 * One top-bar button that opens a list of page links (a disclosure, so links
 * stay plain links for assistive technology). Escape, a click elsewhere or
 * moving focus out closes it.
 */
export default function ConsoleNavigationMenu({
  label,
  routes,
  activeRouteId,
  onNavigate,
}: ConsoleNavigationMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const containsActivePage = routes.some((route) => route.id === activeRouteId);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const closeWhenFocusLeaves = (event: FocusEvent<HTMLDivElement>) => {
    if (!wrapperRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative" onBlur={closeWhenFocusLeaves}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        data-so-selected={containsActivePage || undefined}
        onClick={() => setOpen((current) => !current)}
        className={cx(
          focusRingClass,
          interactiveClass,
          'flex min-h-10 items-center gap-1.5 rounded-control px-3 text-control font-semibold whitespace-nowrap',
          containsActivePage
            ? 'bg-surface-raised text-ink-strong shadow-inset'
            : 'text-ink-muted hover:bg-surface hover:text-ink',
        )}
      >
        {label}
        <ChevronDown
          className={cx('h-4 w-4 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      <ul
        id={listId}
        hidden={!open}
        aria-label={label}
        className="absolute left-0 z-40 mt-2 grid w-64 gap-1 rounded-overlay border border-border-subtle bg-surface-raised p-2 text-ink shadow-overlay"
      >
        {routes.map((route) => {
          const Icon = route.icon;
          const isActive = route.id === activeRouteId;
          return (
            <li key={route.id}>
              <Link
                to={route.path}
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                }}
                aria-current={isActive ? 'page' : undefined}
                data-so-selected={isActive || undefined}
                className={cx(
                  focusRingClass,
                  interactiveClass,
                  'flex min-h-control-comfortable items-center gap-3 rounded-control px-3 text-control font-semibold',
                  isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink hover:bg-surface-recessed',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {route.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
